import { useTemplateLocalStore } from "./use-template-local-store";
import { useTemplateRemoteStore } from "./use-template-remote-store";
import { useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ProjectTemplate } from "PosterEditor/model/project-template";
import { useDuplicateTemplate } from "admin/hooks/use-duplicate-template";

export const useTemplateStore = () => {
  const queryClient = useQueryClient();

  const saveCooldownRef = useRef<NodeJS.Timeout | null>(null);

  const {
    loadTemplates: loadTemplatesLocal,
    loadTemplate: loadTemplateLocal,
    saveTemplate: saveTemplateLocal,
    createTemplate: createTemplateLocal,
    deleteTemplate: deleteTemplateLocal,
    deleteAllTemplates: deleteAlTemplatesLocal,
  } = useTemplateLocalStore();

  const {
    save: saveRemote,
    fetchAllTemplates: fetchRemote,
    fetchTemplatesWithIDs: fetchWithIDsRemote,
    remove: removeRemote,
  } = useTemplateRemoteStore();

  // I think we need to get rid of this fetch-all-templates function.
  // and create a UI driven paged one.
  const {
    data: templates,
    refetch: refetchTemplates,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      try {
        const remoteData = await fetchRemote();
        clearLocalCache();
        if (remoteData.length) {
          remoteData.forEach((remoteTemplate) => {
            saveTemplateLocal(remoteTemplate);
          });
        }
        const data = await loadTemplatesLocal();
        return Array.isArray(data) ? data : [];
      } catch (err) {
        console.error("Failed to fetch templates", err);
        return [];
      }
    },
    enabled: true,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const loadTemplate = (templateId: string) => {
    return loadTemplateLocal(templateId);
  };

  const loadTemplatesByIDs = async (ids: string[]) => {
    const templates = await queryClient.fetchQuery({
      queryKey: ["templates-by-ids", ids.join(",")],
      queryFn: async () => {
        const fetched = await fetchWithIDsRemote(ids);
        if (fetched.length) {
          fetched.forEach((template) => {
            saveTemplateLocal(template);
          });
        }
        return fetched;
      },
      staleTime: 1000 * 60 * 5,
    });

    // 🧠 Match order of `ids`
    const sorted = ids
      .map((id) => templates.find((t) => t.id === id))
      .filter((t): t is ProjectTemplate => Boolean(t));

    return sorted;
  };
  const saveTemplate = async (template: ProjectTemplate, saveNow: boolean) => {
    await saveTemplateLocal(template);
    const updated = { ...template, updatedAt: new Date() };

    queryClient.setQueryData(["templates"], (oldData?: ProjectTemplate[]) => {
      if (!oldData) return [updated]; // If no data, initialize with this template

      return oldData.map((t) => (t.id === updated.id ? updated : t));
    });
    if (saveNow) {
      saveRemote.mutate(updated);
      return;
    }
    if (saveCooldownRef.current) {
      clearTimeout(saveCooldownRef.current);
    }
    saveCooldownRef.current = setTimeout(() => {
      saveRemote.mutate(updated);
      saveCooldownRef.current = null;
    }, 10000);
  };

  const fetchTemplate = async (id: string) => {
    const [template] = await queryClient.fetchQuery({
      queryKey: ["template", id],
      queryFn: async () => await fetchWithIDsRemote([id]),
      staleTime: 1000 * 60 * 5,
    });
    return template;
  };

  //TODO: proper implementation when backend is up.
  const putTemplate = useMutation({
    mutationFn: async (template: ProjectTemplate) =>
      await saveRemote.mutateAsync(template),
  });

  const createTemplate = useMutation({
    mutationFn: async () => {
      const template = await createTemplateLocal();
      await putTemplate.mutateAsync(template);
      return;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (template: ProjectTemplate) => {
      await deleteTemplateLocal(template);
      await removeRemote.mutateAsync(template.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const clearLocalCache = () => {
    deleteAlTemplatesLocal();
  };

  return {
    templates, // Cached templates list.

    // Loads single templat from local storage synchronously.
    // Use if you're sure the project is fetched with fetchProject.
    loadTemplate,

    //Fetched templates from the back end and caches locally.
    loadTemplatesByIDs,

    // Loads single projct from remote and saves it locally.
    fetchTemplate,
    // Saves the project locally.
    saveTemplate,
    // Saves the project to remote asynchronously.
    putTemplate,
    // Async create function.
    createTemplate,
    // Async delete function.
    deleteTemplate,
    // Manual refresh trigger
    refetchTemplates,

    isLoading,
    isFetching,
    isError,
  };
};
